import { useCallback, useEffect, useRef, useState, type DependencyList } from "react";
import { useNotification } from "@/context/NotificationContext";

// Carrega uma lista do servidor ao montar e sempre que deps mudam; recarregar() repete a busca
// (depois de salvar, por exemplo). Falha vira notificação, liga `erro` e mantém a lista anterior. buscar é
// sempre a versão do último render, então enxerga os filtros de agora.
export function useListaRemota<T>(
  buscar: () => Promise<T[] | null | undefined>,
  deps: DependencyList,
  mensagemErro: string,
) {
  const { notify } = useNotification();
  const [itens, setItens] = useState<T[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  // Vira true depois do primeiro carregamento (com sucesso ou erro) e nunca mais volta a false —
  // quem usa o hook troca a tela inteira por um spinner só nessa primeira vez, não a cada recarga
  // (R47: sem isso, uma recarga com a lista vazia desmontava toolbar/filtro/modal aberto por cima).
  const [carregouAlgumaVez, setCarregouAlgumaVez] = useState(false);
  const buscarAtual = useRef(buscar);
  buscarAtual.current = buscar;
  // Chamada mais recente de recarregar(): a resposta de uma chamada anterior, que atrasou, não
  // pode sobrescrever o resultado de uma mais nova (R47 — respostas concorrentes fora de ordem).
  const chamada = useRef(0);

  const recarregar = useCallback(async () => {
    const minha = ++chamada.current;
    setCarregando(true);
    setErro(false);
    try {
      const resultado = (await buscarAtual.current()) ?? [];
      if (minha === chamada.current) setItens(resultado);
    } catch {
      if (minha === chamada.current) {
        setErro(true);
        notify(mensagemErro, "error");
      }
    } finally {
      if (minha === chamada.current) {
        setCarregando(false);
        setCarregouAlgumaVez(true);
      }
    }
  }, [notify, mensagemErro]);

  useEffect(() => {
    recarregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { itens, carregando, erro, carregouAlgumaVez, recarregar };
}
